import React from 'react';
import { BellIcon, CheckIcon, CreditCardIcon, DropletIcon, SparklesIcon, TriangleAlertIcon } from 'lucide-react';
import { bill, priorityInsight, ranges, summary, tips } from '../../data/eau';
import type { EauKind } from '../../hooks/useEauConversation';
interface EauMessageProps {
  kind: EauKind;
  live: boolean;
  onConfigureAlerts: () => void;
  onPay: () => void;
}
const eyebrow = 'flex items-center gap-2 text-sm font-bold text-ville-indigo';
const chipButton = 'inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-ville-navy transition-colors duration-150 ease-ville hover:border-ville-indigo hover:bg-ville-indigo-soft hover:text-ville-indigo focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo';
const primaryButton = 'inline-flex items-center gap-2 rounded-full bg-ville-navy px-6 py-2.5 text-sm font-bold text-white transition-transform duration-150 ease-ville hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2';
export function EauMessage({
  kind,
  live,
  onConfigureAlerts,
  onPay
}: EauMessageProps) {
  if (kind === 'fuite') {
    return <div data-id="element-1209" data-mp="14nsetbtbwa9p">
        <p className="flex items-center gap-2 text-sm font-bold text-ville-coral" data-id="element-1210" data-mp="14na79j17u9mh">
          <TriangleAlertIcon className="h-4 w-4" aria-hidden="true" data-id="element-1211" data-mp="19og0dhysfywj" />
          Débit continu détecté
        </p>
        <p className="mt-2 text-[17px] font-bold text-ville-navy" data-id="element-1212" data-mp="1doqg3mcdu3ol">{priorityInsight.title}.</p>
        <p className="mt-2 text-[15px] leading-relaxed text-slate-600" data-id="element-1213" data-mp="1ims2oh90ol4t">{priorityInsight.detail}</p>

        <div className="mt-4 rounded-2xl border border-slate-200 bg-ville-sand p-4" data-id="element-1214" data-mp="1nfl4oxd3pfd7">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-500" data-id="element-1215" data-mp="1scus67gxjgye">Vérification en 3 minutes</p>
          <ol className="mt-2 space-y-1.5 text-[13px] text-slate-600" data-id="element-1216" data-mp="1xety9p420rz1">
            <li data-id="element-1217" data-mp="21e2m6dcexvmo">1. Fermez tous les robinets de la maison.</li>
            <li data-id="element-1218" data-mp="2a3y8p6sag28">2. Versez du colorant dans le réservoir de la toilette.</li>
            <li data-id="element-1219" data-mp="2acfet9xw7j8">3. Attendez 15 minutes sans tirer la chasse : de la couleur dans la cuvette confirme la fuite.</li>
          </ol>
        </div>

        {live && <div className="mt-5 flex flex-wrap items-center gap-3" data-id="element-1220" data-mp="b3fx5drmb6jo">
            <button type="button" onClick={onConfigureAlerts} className={primaryButton} data-id="element-1221" data-mp="6bxxqu58te0t">
              <BellIcon className="h-4 w-4" aria-hidden="true" data-id="element-1222" data-mp="l0lp0o8231ky" />
              M’alerter si ça se reproduit
            </button>
          </div>}
      </div>;
  }
  if (kind === 'conseils') {
    return <div data-id="element-1223" data-mp="u1s8n6604sif">
        <p className={eyebrow} data-id="element-1224" data-mp="u1jwjghpdr8a">
          <DropletIcon className="h-4 w-4" aria-hidden="true" data-id="element-1225" data-mp="u0qng8v9upmz" />
          Réduire votre consommation
        </p>
        <p className="mt-2 text-[15px] leading-relaxed text-ville-navy" data-id="element-1226" data-mp="pxkhl2c2n0h1">
          Voici les trois gestes les plus payants pour une résidence comme la vôtre.
        </p>
        <ul className="mt-3 space-y-2" data-id="element-1227" data-mp="2rsd16t3swfdz">
          {tips.slice(0, 3).map(tip => <li key={tip.id} className="rounded-2xl border border-slate-200 bg-white px-4 py-3" data-id="element-1228" data-mp="2hmv1p6v8p3gu">
              <p className="text-sm font-bold text-ville-navy" data-id="element-1229" data-mp="2loyfi25dsk5a">{tip.title}</p>
              <p className="mt-0.5 text-[13px] text-slate-600" data-id="element-1230" data-mp="27xjq9dkvrmps">{tip.detail}</p>
              <p className="mt-1 text-[13px] font-semibold text-ville-indigo" data-id="element-1231" data-mp="27vh2l077a5o8">Économie estimée : {tip.saving}</p>
            </li>)}
        </ul>
      </div>;
  }
  if (kind === 'alerte') {
    return <div data-id="element-1232" data-mp="22xyutd4x5wht">
        <p className={eyebrow} data-id="element-1233" data-mp="22yqfvu0w4btu">
          <BellIcon className="h-4 w-4" aria-hidden="true" data-id="element-1234" data-mp="22zs1utjg2iqc" />
          Alertes de consommation
        </p>
        <p className="mt-2 text-[15px] leading-relaxed text-ville-navy" data-id="element-1235" data-mp="1p00jyhpt4i72">
          Je peux vous prévenir en cas de fuite probable, de dépassement de seuil, de nouvelle facture ou de
          risque de gel. Vous choisissez le canal : courriel, texto ou notification mobile.
        </p>
        {live && <button type="button" onClick={onConfigureAlerts} className={`mt-4 ${primaryButton}`} data-id="element-1236" data-mp="3oas3k383woyw">
            <BellIcon className="h-4 w-4" aria-hidden="true" data-id="element-1237" data-mp="1o8itygp32ogh" />
            Configurer mes alertes
          </button>}
      </div>;
  }
  if (kind === 'facture') {
    return <div data-id="element-1238" data-mp="1e9jlay4bisth">
        <p className={eyebrow} data-id="element-1239" data-mp="1e9b978g0rrjc">
          <CreditCardIcon className="h-4 w-4" aria-hidden="true" data-id="element-1240" data-mp="1a9sx9266eqsn" />
          Facture d’eau {bill.reference}
        </p>
        <p className="mt-2 text-[17px] font-bold text-ville-navy" data-id="element-1241" data-mp="15apqndajscik">
          {bill.amount} pour {bill.consumption} — échéance le {bill.dueOn}.
        </p>
        <p className="mt-2 text-[15px] leading-relaxed text-slate-600" data-id="element-1242" data-mp="10hmy98hib93y">
          Période {bill.period}. Le paiement en ligne est confirmé immédiatement et le reçu est déposé dans
          votre profil.
        </p>
        {live && <button type="button" onClick={onPay} className={`mt-4 ${primaryButton}`} data-id="element-1243" data-mp="mkgebal1r4d5">
            <CreditCardIcon className="h-4 w-4" aria-hidden="true" data-id="element-1244" data-mp="hl3tswhne9dq" />
            Payer {bill.amount}
          </button>}
      </div>;
  }
  if (kind === 'suivi') {
    return <div data-id="element-1245" data-mp="2y7a6kxojxpyq">
        <p className={eyebrow} data-id="element-1246" data-mp="2y71tp5sfiknj">
          <CheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-1247" data-mp="2y5z36yz0qk85" />
          C’est noté
        </p>
        <p className="mt-2 text-[15px] leading-relaxed text-ville-navy" data-id="element-1248" data-mp="329yhdawtt6yc">
          Je garde votre compteur à l’œil et je vous écris dès qu’une lecture sort de l’ordinaire.
        </p>
      </div>;
  }
  const week = ranges.semaine;
  return <div data-id="element-1249" data-mp="2afwcam6ubkb4">
      <p className={eyebrow} data-id="element-1250" data-mp="2ejavflq2sgss">
        <SparklesIcon className="h-4 w-4" aria-hidden="true" data-id="element-1251" data-mp="2eijcpbhkudjx" />
        Votre consommation
      </p>
      <p className="mt-2 text-[15px] leading-relaxed text-ville-navy" data-id="element-1252" data-mp="2ji5kh5emz1lc">
        Vous avez utilisé {summary.todayLitres} L aujourd’hui et {summary.monthCubic} m³ depuis le début du
        mois, soit {Math.abs(summary.monthChange)} % de moins que le mois dernier. La moyenne des résidences
        semblables est de {week.benchmark} L par jour.
      </p>
      <p className="mt-2 text-[15px] leading-relaxed text-slate-600" data-id="element-1253" data-mp="1vqgpfhafjz0p">
        Prévision de facture à la fin du mois : {summary.forecastAmount} ({summary.forecastCubic}).
      </p>
    </div>;
}